/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAntiBotStore } from '@/context/AntiBotContext';
import { getGlobalLoadingStore } from '@/context/LoadingContext';
import {
  awaitRecaptchaReady,
  ensureRecaptchaScript,
  resetRecaptchaLoader,
} from '@/shared/utils/recaptcha-loader';
import { resolveAntiBotChallengeKey } from '@/shared/utils/recaptcha-config';
import { resolveChallenge, cancelChallenge } from '@/shared/utils/antibot-challenge-stash';
import { cn } from '@/shared/ui/utils/tailwind';
import { ANTIBOT_CHALLENGE_Z_INDEX } from '@/shared/ui/utils/z-layers';

const MAX_ATTEMPTS = 2;
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;

/**
 * Full-screen overlay that handles the anti-bot challenge step-up.
 *
 * When the backend returns a 428 (challenge required), this overlay renders
 * a visible reCAPTCHA Enterprise CHECKBOX widget. Solving it hands the token
 * to the module-scoped stash (D13) and closes the overlay; the request layer
 * -- not this component -- replays the original request with that token.
 * Cancelling rejects the stash's pending deferred instead of reloading the
 * page, so a request the user gave up on fails cleanly rather than vanishing
 * into a full reload.
 */
/**
 * How long the challenge waits for a cold SDK before giving up.
 *
 * Shorter than the loader's own cold budget on purpose: this overlay is already
 * a recovery screen, and a person staring at a spinner here has less patience
 * than a background token request.
 */
const CHALLENGE_SDK_TIMEOUT_MS = 5_000;

export const AntiBotErrorOverlay: React.FC = () => {
  const { error, clearError } = useAntiBotStore();
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const [challengeError, setChallengeError] = useState(false);
  const [loading, setLoading] = useState(true);

  const attemptsRef = useRef<{ count: number; windowStartedAt: number }>({ count: 0, windowStartedAt: 0 });
  const lastErrorTimestampRef = useRef<number | null>(null);

  const siteKey = error?.siteKey || resolveAntiBotChallengeKey() || '';

  /**
   * Counts this error instance toward the attempt cap (once per distinct
   * `error.timestamp`, since 428s for the same overlay session re-report
   * with a fresh timestamp each time) and resets the window once it elapses.
   *
   * Computed synchronously during render, not via useEffect+useState: the
   * widget-render effect below runs in the same commit as a fresh error, and
   * a state update from an earlier effect in that same commit is not visible
   * to it yet -- that lag let one extra grecaptcha.render() slip through
   * after the cap was already exceeded.
   */
  const isTerminal = useMemo(() => {
    if (!error) return false;
    if (lastErrorTimestampRef.current !== error.timestamp) {
      lastErrorTimestampRef.current = error.timestamp;
      const { count, windowStartedAt } = attemptsRef.current;
      const withinWindow = error.timestamp - windowStartedAt < ATTEMPT_WINDOW_MS;
      attemptsRef.current = withinWindow
        ? { count: count + 1, windowStartedAt }
        : { count: 1, windowStartedAt: error.timestamp };
    }
    return Boolean(error.terminal) || attemptsRef.current.count > MAX_ATTEMPTS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.timestamp, error?.terminal]);

  useEffect(() => {
    if (!error) return;

    try {
      const store = getGlobalLoadingStore();
      if (!store) return;
      const state = store.getState();
      while (state.loadingQueue > 0) {
        state.removeLoading();
      }
    } catch {
      // LoadingContext may not be available in test environments
    }
  }, [error]);

  const renderChallengeWidget = useCallback(async () => {
    if (!containerRef.current || isTerminal) return;

    if (!siteKey) {
      setLoading(false);
      setChallengeError(true);
      return;
    }

    setLoading(true);
    setChallengeError(false);

    // With the SDK loaded on demand, reaching this overlay no longer guarantees
    // the script is in the document: MisconfiguredGuard reports an error before
    // any token was ever requested, and handleCancel below resets the loader.
    // Reading `render` synchronously in that state used to dead-end the user on
    // "Verification failed" with nothing but a Cancel button.
    if (!(window as any).grecaptcha?.enterprise?.render) {
      ensureRecaptchaScript();
      try {
        await awaitRecaptchaReady(CHALLENGE_SDK_TIMEOUT_MS);
      } catch {
        setChallengeError(true);
        setLoading(false);
        return;
      }
    }

    const grecaptcha = (window as any).grecaptcha?.enterprise;
    if (!grecaptcha?.render) {
      setChallengeError(true);
      setLoading(false);
      return;
    }

    try {
      if (widgetIdRef.current !== null) {
        try { grecaptcha.reset(widgetIdRef.current); } catch { /* ignore */ }
      }

      containerRef.current.innerHTML = '';
      widgetIdRef.current = grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          resolveChallenge(token);
          clearError();
        },
        'expired-callback': () => {
          // The token expired before it was used -- the deferred stays
          // pending, so re-render a fresh widget instead of failing outright.
          renderChallengeWidgetRef.current?.();
        },
        'error-callback': () => setChallengeError(true),
      });
      setLoading(false);
    } catch {
      setChallengeError(true);
      setLoading(false);
    }
  }, [clearError, siteKey, isTerminal]);

  // expired-callback needs to call the latest renderChallengeWidget without
  // being captured in a stale closure from the grecaptcha.render() call.
  const renderChallengeWidgetRef = useRef(renderChallengeWidget);
  renderChallengeWidgetRef.current = renderChallengeWidget;

  useEffect(() => {
    if (error && !isTerminal) {
      renderChallengeWidget();
    }
  }, [error, isTerminal, renderChallengeWidget]);

  const handleCancel = useCallback(() => {
    cancelChallenge('User cancelled the anti-bot challenge');
    clearError();
    resetRecaptchaLoader();
    (window as any).__RECAPTCHA_LOAD_FAILED__ = undefined;
  }, [clearError]);

  /* Dialog semantics. This overlay covers the whole viewport above every dialog/drawer and is
   * the one place the product deliberately stops a person mid-task, so it has
   * to behave like a dialog rather than merely look like one: without a role
   * and a label a screen reader announces nothing when it appears, and without
   * a focus trap Tab walks into the page behind an opaque backdrop -- the user
   * is then typing into controls they cannot see. FormDrawer already does this;
   * the pattern is borrowed from there. */
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!error) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => { body.style.overflow = previousOverflow; };
  }, [error]);

  useEffect(() => {
    if (!error) return;
    // Focus the dialog itself, not the Cancel button: landing on the only
    // control that abandons the task invites cancelling by reflex.
    dialogRef.current?.focus();
  }, [error]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      handleCancel();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, [handleCancel]);

  if (!error) return null;

  // The request already stopped waiting, so there is no widget to restart:
  // "try again" means closing this and resubmitting from the untouched form.
  const isExpired = isTerminal && error.reason === 'expired';

  return (
    <div
      data-testid="antibot-error-overlay"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="antibot-challenge-title"
      aria-describedby="antibot-challenge-description"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      // A Radix modal dismisses itself on a `pointerdown` it sees outside its own tree, and this
      // overlay is outside it: solving the challenge would close the form being saved behind it.
      onPointerDown={(event) => event.stopPropagation()}
      // `auto` is required: an open Radix modal sets pointer-events: none on <body>, and this
      // overlay sits outside it, so it would inherit `none` and be visible but unclickable.
      style={{ zIndex: ANTIBOT_CHALLENGE_Z_INDEX, pointerEvents: 'auto' }}
      className={cn(
        'fixed inset-0 flex items-center justify-center outline-none',
        'bg-[var(--surface-primary)]'
      )}
    >
      <div className="text-center max-w-md mx-auto px-4">
        <h2
          id="antibot-challenge-title"
          className="text-xl font-semibold text-[var(--text-primary)] mb-2"
        >
          {t('antiBot.challengeTitle', 'Just confirming it is you')}
        </h2>
        <p
          id="antibot-challenge-description"
          role="status"
          className="text-sm text-[var(--text-secondary)] mb-6"
        >
          {!isTerminal
            ? t('antiBot.challengeDescription', 'For your security, please tick the box below. Your data is still here, nothing is lost.')
            : isExpired
              // Not the same failure: the window closed, their next try can work.
              // Sending someone to support over a timeout wastes their time and yours.
              ? t('antiBot.challengeExpired', 'The verification time ran out. Tap "Try again". Your data is still saved.')
              : t('antiBot.challengeTerminal', 'We could not confirm the verification. Reload the page and try again. If it keeps happening, contact support.')}
        </p>

        {!isTerminal && loading && !challengeError && (
          <div className="h-20 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-[var(--button-primary-bg)] border-t-transparent rounded-full" />
          </div>
        )}

        {!isTerminal && (
          // The widget is a fixed-width block; `text-center` above does not move blocks.
          <div className="flex justify-center">
            <div
              data-testid="antibot-challenge-widget"
              ref={containerRef}
              className={loading && !challengeError ? 'hidden' : ''}
            />
          </div>
        )}

        {!isTerminal && challengeError && (
          <div className="flex flex-col items-center gap-3 mt-4">
            <p className="text-sm text-[var(--text-secondary)]">
              {t('antiBot.challengeFailed', "That didn't work this time. Tick the box again.")}
            </p>
            <button
              data-testid="antibot-retry-widget-button"
              onClick={() => { renderChallengeWidget(); }}
              className={cn(
                'px-4 py-2 text-sm rounded-none cursor-pointer',
                'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]',
                'hover:opacity-90'
              )}
            >
              {t('antiBot.retry', 'Try again')}
            </button>
          </div>
        )}

        <button
          data-testid="antibot-retry-button"
          onClick={handleCancel}
          className={cn(
            'mt-6 px-4 py-2 text-sm rounded-none cursor-pointer',
            'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]',
            'hover:opacity-90'
          )}
        >
          {isExpired
            ? t('antiBot.retry', 'Try again')
            : t('antiBot.cancel', 'Back to the form')}
        </button>
      </div>
    </div>
  );
};
